// Ассеты символов: связывают идентификатор символа с бэкенда с кадрами и скелетом.
//
// Что чему соответствует, объявлено в src/assets/catalog.ts. Здесь таблица превращается
// в готовые текстуры и объекты сцены.

import type { Texture } from 'pixi.js';

import type { GameConfig, SymbolId } from '../api/gameConfig';
import {
  BLURRED_SYMBOLS_SHEET,
  SYMBOLS_SHEET,
  SYMBOL_ASSETS,
  type BlurredSymbolFrame,
  type MappedSymbolId,
  type SymbolAssets,
  type SymbolFrame,
} from '../assets/catalog';
import type { AssetManager, Spine, TextureSheet } from '../assets/manager';

/**
 * Ассеты символа по идентификатору с бэкенда.
 *
 * @throws Error если для символа ничего не объявлено
 */
function assetsOf(id: SymbolId): SymbolAssets {
  const assets: SymbolAssets | undefined = SYMBOL_ASSETS[id as MappedSymbolId];
  if (assets === undefined)
    throw new Error(`Для символа ${id} не объявлены ассеты`);

  return assets;
}

/** Кадры и скелеты символов, готовые к показу. */
export class SymbolLibrary {
  readonly #assets: AssetManager;
  readonly #still: TextureSheet<SymbolFrame>;
  readonly #blurred: TextureSheet<BlurredSymbolFrame>;

  /**
   * Сверяет описание игры с таблицей ассетов сразу при создании: незнакомый символ
   * виден на загрузке, а не в момент, когда он должен появиться на барабане.
   *
   * @throws Error если у символа из описания игры нет ассетов
   */
  constructor(assets: AssetManager, config: GameConfig) {
    this.#assets = assets;
    this.#still = assets.sheet(SYMBOLS_SHEET);
    this.#blurred = assets.sheet(BLURRED_SYMBOLS_SHEET);

    for (const symbol of config.symbols)
      assetsOf(symbol.id);
  }

  /** Кадр символа на остановленном барабане. */
  still(id: SymbolId): Texture {
    return this.#still[assetsOf(id).still];
  }

  /**
   * Кадр символа на крутящемся барабане.
   *
   * Символ без своего размытого кадра отдаёт обычный: в полосы барабанов он не входит
   * и в прокрутке не участвует, но пустое место на экране всё равно недопустимо.
   */
  blurred(id: SymbolId): Texture {
    const frame: BlurredSymbolFrame | null = assetsOf(id).blurred;
    if (frame === null)
      return this.still(id);

    return this.#blurred[frame];
  }

  /** Новый объект сцены с анимациями символа. Разобранный скелет общий для всех таких объектов. */
  spine(id: SymbolId): Spine {
    return this.#assets.spine(assetsOf(id).skeleton);
  }
}
